import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { rangeArray } from '../../../../utils/helpers';
import DivButton from '../../../common/DivButton';
import LinePrice from '../../Pick/basket/LinePrice';
import { LineNumbers } from '../../Types/Types';
import {
    compareNumbers,
    generatePermutations,
    PRIMARY_NUMBERS,
    RANGE_FROM,
    RANGE_TO,
    toggleNumber,
} from '../../helpers/linesHelper';
import useLinesHelper from '../../helpers/useLinesHelper';
import { RootState } from '../../redux/store';
import NumberPanelTitle from '../picker/NumberPanelTitle';

import ChooseLotteryMode from './ChooseLotteryMode';

const LotteryPermsPicker = () => {
    const { t } = useTranslation();
    const {
        ranges,
        lines,
        numbersPerLine,
        setLines,
        setStakes,
        stakes,
        setCurrentPickerIndex,
        banker,
        setBanker,
        permsPickerLines,
        isPermsPickerEnabled,
        setEmptyLine,
        setLineStake,
        triggerChooseLotteryMode,
        setPermsPickerLines,
        setPermutations,
        setIsPermsPickerEnabled,
    } = useLinesHelper();

    const { stakeMin, stakeMax, ticketPriceMin, ticketPriceMax } = useSelector((state: RootState) => state.playStore);
    const [showMaxNumbersMessage, setShowMaxNumbersMessage] = useState(false);
    const [calculatedPermutations, setCalculatedPermutations] = useState<LineNumbers>([]);

    const { linePriceGross } = useSelector((state: RootState) => state.playStore);

    const MAX_VALUE = 10000;
    const MAX_PERMUTATIONS_NUMBER = 15; // TODO: Get this value from API
    const [pressTimer, setPressTimer] = useState<NodeJS.Timeout | null>(null);
    const [isLongPress, setIsLongPress] = useState(false);
    const [isMaxLinesReached, setIsMaxLinesReached] = useState(false);
    const { isChooseLotteryModeTriggered } = useSelector((state: RootState) => state.lotteryPlayStore);
    const handleCloseChooseLotteryMode = () => triggerChooseLotteryMode(false);

    const handlePermsButtonClick = () => {
        setIsPermsPickerEnabled(true);
        setPermsPickerLines(lines[0]);
        setPermutations(lines[0][0]);
        triggerChooseLotteryMode(false);
    };

    useLayoutEffect(() => {
        setLines([[[]]]);
    }, []);

    useEffect(() => {
        if (!lines || !numbersPerLine) {
            return;
        }

        if (permsPickerLines.length > 0) {
            const permutations = generatePermutations(
                permsPickerLines[PRIMARY_NUMBERS],
                numbersPerLine[PRIMARY_NUMBERS],
                banker
            );
            setCalculatedPermutations(permutations);
        }
    }, [permsPickerLines, numbersPerLine, banker]);

    useEffect(() => {
        if (!isPermsPickerEnabled) {
            return;
        }

        if (calculatedPermutations.length > 0) {
            const wrapPermutations = calculatedPermutations.map((item) => [item]);
            setLines(wrapPermutations);
        } else {
            setLines([[[]]]);
        }
    }, [calculatedPermutations]);

    useEffect(() => {
        if (isPermsPickerEnabled) {
            const basicStake = stakes[PRIMARY_NUMBERS];
            const updatedStakes = stakes.map(() => basicStake);

            if (JSON.stringify(updatedStakes) !== JSON.stringify(stakes)) {
                setStakes(updatedStakes);
            }
        }
    }, [isPermsPickerEnabled, stakes]);

    useEffect(() => {
        if (!calculatedPermutations || !linePriceGross || !MAX_VALUE) {
            return;
        }
        let permutationsLength = 0;
        if (permsPickerLines.length > 0) {
            permutationsLength = permsPickerLines[PRIMARY_NUMBERS].length;
        }

        setIsMaxLinesReached(permutationsLength > MAX_PERMUTATIONS_NUMBER);
    }, [calculatedPermutations, banker, linePriceGross, MAX_VALUE, numbersPerLine, isMaxLinesReached]);

    useEffect(() => {
        if (
            isPermsPickerEnabled &&
            permsPickerLines[PRIMARY_NUMBERS] &&
            permsPickerLines[PRIMARY_NUMBERS].length >= MAX_PERMUTATIONS_NUMBER
        ) {
            setShowMaxNumbersMessage(true);
        } else {
            setShowMaxNumbersMessage(false);
        }
    }, [permsPickerLines[PRIMARY_NUMBERS], isPermsPickerEnabled]);
    const updateLocalLineNumbers = (index: number, newLine: number[]) => {
        const updatedLines = permsPickerLines.map((numbers, i) => (i === index ? newLine : numbers));
        setLines([updatedLines]);
    };
    const handleBallClick = (targetNumber: number, numbersIndex: number) => {
        if (
            lines[PRIMARY_NUMBERS][0].length === numbersPerLine[PRIMARY_NUMBERS] &&
            !lines[PRIMARY_NUMBERS][0].includes(targetNumber) &&
            !isPermsPickerEnabled
        ) {
            triggerChooseLotteryMode(true);
            return;
        }

        if (isLongPress) {
            setIsLongPress(false);
            return;
        }

        const currentNumbers = isPermsPickerEnabled
            ? permsPickerLines[PRIMARY_NUMBERS]
            : lines[numbersIndex]?.[PRIMARY_NUMBERS];
        if (banker === targetNumber) {
            setBanker(null);
            updateLocalLineNumbers(numbersIndex, currentNumbers);
            return;
        }

        if (isPermsPickerEnabled) {
            const isSelected = currentNumbers.includes(targetNumber);

            if (isSelected) {
                const updatedNumbers = currentNumbers.filter((value) => value !== targetNumber);
                setPermsPickerLines([updatedNumbers]);
                setPermutations(updatedNumbers);
            } else if (currentNumbers.length < MAX_PERMUTATIONS_NUMBER) {
                const updatedNumbers = [...currentNumbers, targetNumber].sort(compareNumbers);
                setPermsPickerLines([updatedNumbers]);
                setPermutations(updatedNumbers);
            }
        } else {
            const newLineNumbers = lines?.[PRIMARY_NUMBERS]?.map((number, index) =>
                index === numbersIndex ? toggleNumber(number, targetNumber, numbersPerLine[numbersIndex]) : number
            );
            setCurrentPickerIndex(0);
            setLines([newLineNumbers]);
        }
    };

    const handleBallLongPress = (targetNumber: number, numbersIndex: number) => {
        if (isPermsPickerEnabled) {
            setIsLongPress(true);

            const currentNumbers = permsPickerLines[numbersIndex];

            if (currentNumbers.length >= MAX_PERMUTATIONS_NUMBER && !currentNumbers.includes(targetNumber)) {
                return;
            }
            const newLineNumbers = currentNumbers.includes(targetNumber)
                ? currentNumbers
                : [...currentNumbers, targetNumber].sort(compareNumbers);

            setPermsPickerLines([newLineNumbers]);
            setBanker(targetNumber);

            updateLocalLineNumbers(numbersIndex, newLineNumbers);
        }
    };

    const startPressTimer = (targetNumber: number, numbersIndex: number) => {
        const timer = setTimeout(() => {
            handleBallLongPress(targetNumber, numbersIndex);
        }, 300);

        setPressTimer(timer);
    };

    const cancelPressTimer = () => {
        if (pressTimer) {
            clearTimeout(pressTimer);
            setPressTimer(null);
        }
    };

    const getBallClassName = (i: number) => {
        const isSelected = isPermsPickerEnabled
            ? permsPickerLines[PRIMARY_NUMBERS].includes(i)
            : lines[PRIMARY_NUMBERS]?.[0]?.includes(i);
        const isBanker = banker === i;

        const isDisabled = !isSelected && !isBanker && isMaxLinesReached;

        return `number text-center purchase-line-number purchase-line-chooseable ${isSelected ? 'choosed' : ''} ${
            isBanker ? 'ball_banker' : ''
        } ${isDisabled ? 'ball_disabled' : ''}`.trim();
    };

    if (!ranges[PRIMARY_NUMBERS] || !Array.isArray(ranges[PRIMARY_NUMBERS])) {
        return null;
    }

    const range = rangeArray(ranges[PRIMARY_NUMBERS][RANGE_FROM], ranges[PRIMARY_NUMBERS][RANGE_TO]);

    return (
        <>
            <ChooseLotteryMode
                show={isChooseLotteryModeTriggered}
                onHide={handleCloseChooseLotteryMode}
                onPermsButtonClick={handlePermsButtonClick}
            />
            <div className="play-picker perms-and-bankers-picker mx-auto">
                {isPermsPickerEnabled && (
                    <div className="content-container p-4">
                        <p className="text-center">
                            {t('playPicker.keno.pickNumbersWithinRange', {
                                from: ranges[PRIMARY_NUMBERS][RANGE_FROM],
                                to: ranges[PRIMARY_NUMBERS][RANGE_TO],
                            })}
                        </p>
                        {Array.isArray(permsPickerLines) && permsPickerLines?.[PRIMARY_NUMBERS]?.length > 0 && (
                            <div className="balls row justify-content-center">
                                {permsPickerLines[PRIMARY_NUMBERS].map((number) => (
                                    <div
                                        key={number}
                                        className={`ball ball_selected ${number === banker ? 'ball_banker' : ''}`}
                                    >
                                        {number}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="lines-details mt-3">
                            {calculatedPermutations.length > 0 ? (
                                <p>
                                    <strong>
                                        {numbersPerLine[PRIMARY_NUMBERS]} {t('playPicker.permsAndBankers.numbers')},{' '}
                                        {calculatedPermutations.length}{' '}
                                        {calculatedPermutations.length === 1
                                            ? t('playPicker.permsAndBankers.line')
                                            : t('playPicker.permsAndBankers.lines')}
                                    </strong>
                                </p>
                            ) : (
                                <p>{t('playPicker.permsAndBankers.selectNumbers')}</p>
                            )}
                            <p>({t('playPicker.permsAndBankers.addBankerInfo')})</p>
                        </div>
                    </div>
                )}
                <>
                    {!isPermsPickerEnabled && (
                        <p className="font-bold text-center">
                            <NumberPanelTitle />
                        </p>
                    )}
                </>
                {showMaxNumbersMessage && (
                    <p className="text-danger text-center mb-2"> {t('playPicker.permsAndBankers.reachedMaxLines')}</p>
                )}
                <div className="row row-buttons no-gutters d-flex justify-content-between">
                    <div className="mx-auto">
                        <button
                            type="button"
                            onClick={() => setEmptyLine(0)}
                            className="btn btn-secondary purchase-line-clear"
                        >
                            {t('playPicker.clear')}
                        </button>
                    </div>
                </div>
                <div className="purchase-line">
                    <div className="row row-numbers no-gutters purchase-line-numbers">
                        {range.map((n) => (
                            <DivButton
                                key={n}
                                onClick={() => handleBallClick(n, PRIMARY_NUMBERS)}
                                onMouseDown={() => startPressTimer(n, PRIMARY_NUMBERS)}
                                onMouseUp={cancelPressTimer}
                                onMouseLeave={cancelPressTimer}
                                onTouchStart={() => startPressTimer(n, PRIMARY_NUMBERS)}
                                onTouchEnd={cancelPressTimer}
                                className={getBallClassName(n)}
                            >
                                {' '}
                                {n}{' '}
                            </DivButton>
                        ))}
                    </div>
                    <LinePrice
                        index={0}
                        stake={1}
                        stakeMin={stakeMin}
                        stakeMax={stakeMax}
                        linePriceGross={linePriceGross}
                        setLineStake={setLineStake}
                        ticketPriceMin={ticketPriceMin}
                        ticketPriceMax={ticketPriceMax}
                    />
                </div>
            </div>
        </>
    );
};

export default LotteryPermsPicker;
